import React, { useEffect, useState } from "react";
import { dateLocale } from "@/lib/dateTime";
import { api, unwrapObject } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dateRangeBounds } from "@/lib/filters";
import type { WaReport } from "@/types/whatsapp";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "last_7", label: "Last 7 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last_30", label: "Last 30 days" },
  { value: "this_quarter", label: "This quarter" },
];

const minutesLabel = (m: number | null) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h} h ${m % 60} min` : `${Math.floor(h / 24)} d ${h % 24} h`;
};
const inr = (v: number) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const CATEGORY: Record<string, string> = {
  marketing: "Marketing",
  utility: "Utility",
  authentication: "Authentication",
  service: "Service (replies)",
  unknown_template: "Template (category not reported yet)",
};

/** Team WhatsApp performance: headline numbers, per-rep table, daily volume and the cost estimate */
export function WhatsAppReports() {
  const [range, setRange] = useState("last_30");
  const [report, setReport] = useState<WaReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const [from, to] = dateRangeBounds(range) || [null, null];
    const qs = new URLSearchParams();
    if (from) qs.set("from", from.toISOString());
    if (to) qs.set("to", to.toISOString());
    setLoading(true);
    api
      .get(`/api/WhatsApp/Report?${qs}`)
      .then((res) => setReport(unwrapObject<WaReport>(res)))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [range]);

  const t = report?.totals;
  const tiles = t
    ? [
        { label: "Chats", value: t.chats.toLocaleString("en-IN") },
        { label: "Messages received", value: t.received.toLocaleString("en-IN") },
        { label: "Messages sent", value: t.sent.toLocaleString("en-IN") },
        { label: "Median first response", value: minutesLabel(t.median_response_minutes) },
        { label: "Waiting for a reply", value: t.unanswered.toLocaleString("en-IN") },
        { label: "Estimated WhatsApp cost", value: inr(t.estimated_cost) },
      ]
    : [];

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Your scope: own chats for executives, the team for sales heads, everything for admins.</p>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="h-9 w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : !report ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Could not load the report.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 @2xl/main:grid-cols-3 @6xl/main:grid-cols-6 gap-3">
            {tiles.map((tile) => (
              <div key={tile.label} className="bg-card border rounded-xl p-4">
                <div className="text-xs text-muted-foreground">{tile.label}</div>
                <div className="text-2xl font-semibold text-foreground mt-1 tabular-nums">{tile.value}</div>
              </div>
            ))}
          </div>

          {/* Per rep */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b text-sm font-semibold">By rep</div>
            {report.reps.length === 0 ? (
              <div className="px-4 py-6 text-sm text-muted-foreground">No WhatsApp activity in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr className="border-b">
                      {["Rep", "Chats", "Received", "Sent", "Templates", "Failed", "Answered", "Waiting", "Avg first response", "Median"].map((h, i) => (
                        <th key={h} className={`px-4 py-2 font-medium ${i ? "text-right" : "text-left"}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y tabular-nums">
                    {report.reps.map((r) => (
                      <tr key={r.user_id || "unassigned"}>
                        <td className="px-4 py-2 font-medium">{r.name}</td>
                        <td className="px-4 py-2 text-right">{r.chats}</td>
                        <td className="px-4 py-2 text-right">{r.received}</td>
                        <td className="px-4 py-2 text-right">{r.sent}</td>
                        <td className="px-4 py-2 text-right">{r.templates}</td>
                        <td className="px-4 py-2 text-right">{r.failed}</td>
                        <td className="px-4 py-2 text-right">{r.answered}</td>
                        <td className="px-4 py-2 text-right">{r.unanswered}</td>
                        <td className="px-4 py-2 text-right">{minutesLabel(r.avg_response_minutes)}</td>
                        <td className="px-4 py-2 text-right">{minutesLabel(r.median_response_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="px-4 py-2 border-t text-[11px] text-muted-foreground">
              First response = time from a customer's first message (after your last reply) to the next reply by a person. Automatic replies don't count.
            </p>
          </div>

          <div className="grid gap-4 @4xl/main:grid-cols-2">
            {/* Daily volume */}
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold">Daily messages</div>
              {report.daily.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground">No messages in this period.</div>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground sticky top-0 bg-card">
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left font-medium">Day</th>
                        <th className="px-4 py-2 text-right font-medium">Received</th>
                        <th className="px-4 py-2 text-right font-medium">Sent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y tabular-nums">
                      {[...report.daily].reverse().map((d) => (
                        <tr key={d.day}>
                          <td className="px-4 py-2">
                            {new Date(`${d.day}T00:00:00`).toLocaleDateString(dateLocale(), { weekday: "short", day: "2-digit", month: "short" })}
                          </td>
                          <td className="px-4 py-2 text-right">{d.received}</td>
                          <td className="px-4 py-2 text-right">{d.sent}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Cost */}
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold">Estimated WhatsApp cost</div>
              {report.cost.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground">No messages sent in this period.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr className="border-b">
                      <th className="px-4 py-2 text-left font-medium">Category</th>
                      <th className="px-4 py-2 text-right font-medium">Sent</th>
                      <th className="px-4 py-2 text-right font-medium">Charged</th>
                      <th className="px-4 py-2 text-right font-medium">Estimate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y tabular-nums">
                    {report.cost.map((c) => (
                      <tr key={c.category}>
                        <td className="px-4 py-2">{CATEGORY[c.category] || c.category}</td>
                        <td className="px-4 py-2 text-right">{c.messages}</td>
                        <td className="px-4 py-2 text-right">{c.billable}</td>
                        <td className="px-4 py-2 text-right font-medium">{inr(c.estimated_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="px-4 py-2 border-t text-[11px] text-muted-foreground">
                WhatsApp charges template messages by category. Rates are set in Settings → WhatsApp → Automation.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
